/**
 * Listings Query Hooks
 * 
 * TanStack Query-based data fetching for broker listings with:
 * - Global prefetching on nav hover for zero-latency navigation
 * - Stale-while-revalidate pattern
 * - Optimistic updates for CRUD operations
 * - Background new listings detection
 */

import { useEffect, useCallback } from 'react';
import { 
  useQuery, 
  useMutation, 
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { queryKeys } from '../lib/queryClient';
import { Apartment, MeetingRequest } from '../types';
import { getMeetingsForLandlord } from '../lib/meetings';
import { fetchAgreementCounts } from '../lib/brokerAgreements';

// Extend query keys for listings
const listingsKeys = {
  all: ['listings'] as const,
  byUser: (userId: string) => ['listings', userId] as const,
  withFilters: (userId: string, listingType: string) => ['listings', userId, listingType] as const,
};

export interface ListingsFilters {
  listingType: 'all' | 'rent' | 'sale';
}

export interface ListingsData {
  apartments: Apartment[];
  meetings: MeetingRequest[];
  questionCounts: Record<string, number>;
  likeCounts: Record<string, number>;
  agreementCounts: Record<string, number>;
}

/**
 * Core fetch function for listings data
 * Extracted to be reusable by both query and prefetch
 */
async function fetchListingsData(userId: string): Promise<ListingsData> {
  // Fetch apartments and meetings in parallel
  const [apartmentsResult, meetingsData] = await Promise.all([
    supabase
      .from('apartments')
      .select(`
        id, title, price, address, city, neighborhood, rooms, sqm, floor,
        images, amenities, petFriendly, description, idealRenterDescription,
        owner_id, view_count, like_count, unanswered_question_count,
        requires_broker_agreement, custom_amenities, custom_appliances,
        listing_type, price_per_sqm, built_year,
        lease_duration, move_in_date, security_deposit, vaad_bayit, arnona,
        elevator, renovated, private_parking, storage, safety_room,
        first_floor_bars, furnished, building_floors, sun_direction, appliances,
        latitude, longitude, google_place_id, landlord
      `)
      .eq('owner_id', userId),
    getMeetingsForLandlord(userId)
  ]);

  const { data: apartmentsData, error: apartmentsError } = apartmentsResult;
  if (apartmentsError) throw apartmentsError;

  const apartments = apartmentsData || [];
  
  // Extract counts from denormalized columns
  const questionCounts: Record<string, number> = {};
  const likeCounts: Record<string, number> = {};
  
  apartments.forEach(apt => {
    questionCounts[apt.id] = apt.unanswered_question_count ?? 0;
    likeCounts[apt.id] = apt.like_count || 0;
  });
  
  // Fetch agreement counts for all apartments
  let agreementCounts: Record<string, number> = {};
  if (apartments.length > 0) {
    const apartmentIds = apartments.map(apt => apt.id);
    agreementCounts = await fetchAgreementCounts(apartmentIds);
  }
  
  return {
    apartments,
    meetings: meetingsData,
    questionCounts,
    likeCounts,
    agreementCounts,
  };
}

/**
 * Custom hook to get current user ID
 * Returns null if not authenticated
 */
function useCurrentUserId() {
  const { data: userId } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id ?? null;
    },
    staleTime: Infinity, // User ID doesn't change during session
    gcTime: Infinity,
  });
  return userId ?? null;
}

/**
 * Main listings query hook
 * 
 * Features:
 * - Automatic caching and deduplication
 * - Background revalidation when stale
 * - Keeps previous data during refetch (no flash)
 */
export function useListings() {
  const userId = useCurrentUserId();
  
  return useQuery({
    queryKey: listingsKeys.byUser(userId ?? 'anonymous'),
    queryFn: () => fetchListingsData(userId!),
    enabled: !!userId,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000, // 1 minute - listings change less frequently
    gcTime: 10 * 60 * 1000, // 10 minutes cache retention
  });
}

/**
 * Prefetch listings on nav hover
 * Call this when user hovers over the Listings nav item
 */
export function usePrefetchListings() {
  const queryClient = useQueryClient();
  
  const prefetch = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    // Prefetch listings data into the cache
    await queryClient.prefetchQuery({
      queryKey: listingsKeys.byUser(user.id),
      queryFn: () => fetchListingsData(user.id),
      staleTime: 60 * 1000,
    });
  }, [queryClient]);
  
  return prefetch;
}

/**
 * Hook for detecting new listings (for delta sync)
 * Returns count of new listings since last refresh
 */
export function useNewListingsSubscription(
  onNewListing: (listing: Apartment) => void
) {
  const userId = useCurrentUserId();
  
  useEffect(() => {
    if (!userId) return;
    
    const channel = supabase
      .channel('listings-new-detection')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'apartments',
          filter: `owner_id=eq.${userId}`,
        },
        (payload) => {
          onNewListing(payload.new as Apartment);
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, onNewListing]);
}

/**
 * Hook for listening to listing updates (badge counts)
 */
export function useListingsUpdateSubscription(
  onUpdate: (updated: Partial<Apartment> & { id: string }) => void
) {
  const userId = useCurrentUserId();
  
  useEffect(() => {
    if (!userId) return;
    
    const channel = supabase
      .channel('listings-badge-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'apartments',
          filter: `owner_id=eq.${userId}`,
        },
        (payload) => {
          onUpdate(payload.new as Partial<Apartment> & { id: string });
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, onUpdate]);
}

/**
 * Mutation hook for deleting a listing
 * 
 * Features:
 * - Optimistic update - UI changes instantly
 * - Automatic rollback on error
 */
export function useDeleteListingMutation() {
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();
  
  return useMutation({
    mutationFn: async (listingId: string) => {
      const { error } = await supabase
        .from('apartments')
        .delete()
        .eq('id', listingId);
      
      if (error) throw error;
      return listingId;
    },
    
    // Optimistic update
    onMutate: async (listingId: string) => {
      await queryClient.cancelQueries({ 
        queryKey: listingsKeys.byUser(userId ?? 'anonymous') 
      });
      
      const previousData = queryClient.getQueryData<ListingsData>(
        listingsKeys.byUser(userId ?? 'anonymous')
      );
      
      // Optimistically remove listing
      queryClient.setQueryData<ListingsData>(
        listingsKeys.byUser(userId ?? 'anonymous'),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            apartments: old.apartments.filter(apt => apt.id !== listingId),
          };
        }
      );
      
      return { previousData };
    },
    
    onError: (_err, _listingId, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          listingsKeys.byUser(userId ?? 'anonymous'),
          context.previousData
        );
      }
    },
    
    onSettled: () => {
      queryClient.invalidateQueries({ 
        queryKey: listingsKeys.byUser(userId ?? 'anonymous') 
      });
    },
  });
}

/**
 * Hook to invalidate listings cache
 * Call after mutations that affect listings
 */
export function useInvalidateListings() {
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();
  
  return useCallback(() => {
    if (userId) {
      queryClient.invalidateQueries({ 
        queryKey: listingsKeys.byUser(userId) 
      });
    }
  }, [queryClient, userId]);
}

/**
 * Hook to update local cache optimistically
 * Used for real-time updates without full refetch
 */
export function useUpdateListingsCache() {
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();
  
  const updateApartment = useCallback((updated: Partial<Apartment> & { id: string }) => {
    queryClient.setQueryData<ListingsData>(
      listingsKeys.byUser(userId ?? 'anonymous'),
      (old) => {
        if (!old) return old;
        return {
          ...old,
          apartments: old.apartments.map(apt => 
            apt.id === updated.id ? { ...apt, ...updated } : apt
          ),
          questionCounts: updated.unanswered_question_count !== undefined
            ? { ...old.questionCounts, [updated.id]: updated.unanswered_question_count }
            : old.questionCounts,
          likeCounts: updated.like_count !== undefined
            ? { ...old.likeCounts, [updated.id]: updated.like_count }
            : old.likeCounts,
        };
      }
    );
  }, [queryClient, userId]);
  
  const addApartment = useCallback((newApt: Apartment) => {
    queryClient.setQueryData<ListingsData>(
      listingsKeys.byUser(userId ?? 'anonymous'),
      (old) => {
        if (!old) return old;
        // Check if already exists (avoid duplicates)
        if (old.apartments.some(apt => apt.id === newApt.id)) return old;
        return {
          ...old,
          apartments: [newApt, ...old.apartments],
          questionCounts: { ...old.questionCounts, [newApt.id]: 0 },
          likeCounts: { ...old.likeCounts, [newApt.id]: 0 },
        };
      }
    );
  }, [queryClient, userId]);
  
  return { updateApartment, addApartment };
}
